/* eslint-disable max-len */
import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import cx from 'classnames';

import { ReactComponent as Search } from 'app/components/svg/Search.svg';
import { ReactComponent as Close } from 'app/components/svg/Close.svg';
import Dropdown from 'app/components/ui/Dropdown';
import { withDropdown } from 'app/utils/withDropdown';
import { ComponentProps } from 'app/types/dropdown';
import Input from 'app/components/ui/Input';
import Button from 'app/components/ui/Button';
import { useGetSearchDataLazyQuery } from 'app/generated/graphql';

import s from './InputSearch.module.sass';

export type FormTypes = {
  search: string
};

type InputSearchProps = {
  className?: string
  isBurgerActive?: boolean
};

const InputSearch: React.FC<ComponentProps & InputSearchProps> = ({
  dropdownRef,
  active,
  setActive,
  dynamicId,
  className,
  isBurgerActive,
}) => {
  const {
    handleSubmit,
    formState: { errors },
    getValues,
    clearErrors,
    control,
    setValue,
    register,
  } = useForm<FormTypes>({ defaultValues: { search: '' } });

  const param = useParams();

  const initialState = useMemo(() => ({
    search: {
      tokens: [
        {
          tokenId: 0,
          id: 'KT1BHCumksALJQJ8q8to2EPigPW6qpyTr7Ng',
          name: 'CRUNCH',
          symbol: 'CRUNCH',
          icon: 'ipfs://bafybeienhhbxz53n3gtg7stjou2zs3lmhupahwovv2kxwh5uass3bc5xzq',
        },
        {
          tokenId: 19,
          id: 'KT18fp5rcTW7mbWDmzFwjLDUhs5MeJmagDSZ',
          name: 'Wrapped WBTC',
          symbol: 'wBTC',
          icon: 'ipfs://Qmdj6n9T48LDWex8NkBMKUQJfZgardxZVdtRRibYQVzLCJ',
        },
      ],
      pairs: [
        {
          id: 'KT1RRgK6eXvCWCiEGWhRZCSVGzhDzwXEEjS4',
          version: 'Old',
          token1: {
            symbol: 'XTZ',
            icon: 'ipfs://Qmf3brydfr8c6CKGUUu73Dd7wfBw66Zbzof5E1BWGeU222',
          },
          token2: {
            symbol: 'CRUNCH',
            icon: 'ipfs://bafybeienhhbxz53n3gtg7stjou2zs3lmhupahwovv2kxwh5uass3bc5xzq',
          },
        },
        {
          id: 'KT1DksKXvCBJN7Mw6frGj6y6F3CbABWZVpj1',
          version: 'Old',
          token1: {
            symbol: 'XTZ',
            icon: 'ipfs://Qmf3brydfr8c6CKGUUu73Dd7wfBw66Zbzof5E1BWGeU222',
          },
          token2: {
            symbol: 'wWBTC',
            icon: 'ipfs://Qmdj6n9T48LDWex8NkBMKUQJfZgardxZVdtRRibYQVzLCJ',
          },
        },
      ],
    },
  }), []);

  const [fetchData, { data: searchData = initialState, loading }] = useGetSearchDataLazyQuery();

  const onSubmit = (data: FormTypes) => {
    fetchData({
      variables: {
        address: data.search,
      },
    });
    setActive(true);
  };

  const handleClearInput = () => {
    setValue('search', '');

    clearErrors('search');

    if (setActive) {
      setActive(false);
    }
  };

  const handleActiveByFocus = () => {
    if (setActive) {
      setActive(true);
    }
  };

  useEffect(() => {
    setActive(false);
  }, [param, setActive]);

  useEffect(() => {
    if (isBurgerActive) {
      setActive(false);
    }
  }, [isBurgerActive, setActive]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={cx(s.form, className)}>
      <div id={dynamicId} className={cx(s.root)}>
        <div className={cx(s.inputWrapper, { [s.disabled]: false }, { [s.error]: !!errors.search })}>
          {!getValues().search && (
            <div className={s.svgSearchWrapper}>
              <Search className={cx(s.svgSearch, { [s.active]: active })} />
            </div>
          )}

          <Controller
            render={({ field }) => (
              <Input
                {...register('search', { required: 'There is no such address' })}
                active={active}
                placeholder={errors.search?.message ? errors.search.message : 'Search tokens or pairs by address...'}
                onFocus={handleActiveByFocus}
                error={!!errors.search}
                className={cx(s.input, { [s.pressed]: true })}
                {...field}
              />
            )}
            name="search"
            control={control}
          />

          {(active || getValues().search) && (
          <Button
            theme="clear"
            sizeT="small"
            className={cx(s.close)}
            onClick={handleClearInput}
          >
            <Close />
          </Button>
          )}
        </div>

        {active && (
        <Dropdown
          data={{ tokens: searchData?.search.tokens, pairs: searchData?.search.pairs }}
          ref={dropdownRef}
          loading={loading}
        />
        )}
      </div>

      <input type="submit" className={cx(s.submit)} />
    </form>
  );
};

export default withDropdown(InputSearch);
